import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Gavel, Loader2, ShieldAlert, Paperclip, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COMPLIANCE = ["compliance_director", "super_admin", "compliance_reviewer"];
const STATUS = {
  open: "bg-amber-100 text-amber-800", appealed: "bg-orange-100 text-orange-800",
  resolved: "bg-emerald-100 text-emerald-700", appeal_resolved: "bg-emerald-100 text-emerald-700",
  withdrawn: "bg-slate-100 text-slate-500",
};

export default function DisputesPanel({ deal }) {
  const { user } = useAuth();
  const isCompliance = COMPLIANCE.includes(user?.role);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ category: "delivery", claim: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${deal.deal_id}/disputes`);
      setDisputes(data.disputes || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [deal.deal_id]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const open = () => {
    if (!form.claim.trim()) { toast.error("Describe the claim"); return; }
    act("open", () => api.post(`/deals/${deal.deal_id}/disputes`, form), "Dispute opened — deal placed on hold");
  };
  const addEvidence = (d) => {
    const note = window.prompt("Evidence description:");
    if (!note) return;
    act(`ev-${d.dispute_id}`, () => api.post(`/disputes/${d.dispute_id}/evidence`, { type: "document", ref: "vault", note }), "Evidence added");
  };
  const resolve = (d) => {
    const findings = window.prompt("Findings:"); if (!findings) return;
    const reason = window.prompt("Resolution reason:"); if (!reason) return;
    act(`res-${d.dispute_id}`, () => api.post(`/disputes/${d.dispute_id}/resolve`, {
      findings, resolution_reason: reason, outcome: "in_favor_of_buyer", evidence_refs: [], contract_refs: [] }), "Dispute resolved");
  };
  const appeal = (d) => {
    const reason = window.prompt("Reason for appeal:"); if (!reason) return;
    act(`app-${d.dispute_id}`, () => api.post(`/disputes/${d.dispute_id}/appeal`, { reason }), "Appeal filed");
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="disputes-panel">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Gavel size={15} /> Dispute center</h4>

      <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-4" data-testid="dispute-open-form">
        <select data-testid="dispute-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="h-9 rounded-md border border-slate-300 px-2 text-sm">
          {["delivery", "quality", "quantity", "payment", "documentation", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input data-testid="dispute-claim" value={form.claim} onChange={(e) => setForm({ ...form, claim: e.target.value })}
          placeholder="Describe the claim" className="h-9 rounded-md border border-slate-300 px-3 text-sm sm:col-span-2" />
        <button data-testid="dispute-open-btn" onClick={open} disabled={busy === "open"}
          className="h-9 rounded-md bg-[#DC2626] px-3 text-sm font-medium text-white disabled:opacity-60">Open dispute</button>
      </div>

      {loading ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-3" data-testid="disputes-list">
          {disputes.length === 0 && <p className="text-sm text-slate-400">No disputes on this deal.</p>}
          {disputes.map((d) => (
            <div key={d.dispute_id} data-testid={`dispute-${d.dispute_id}`} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-[#0A0F1C]">Case {d.dispute_id} · {d.category}</span>
                <span data-testid={`dispute-status-${d.dispute_id}`} className={`rounded px-2 py-0.5 text-xs ${STATUS[d.status] || "bg-slate-100 text-slate-600"}`}>{d.status?.replace(/_/g, " ")}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600"><span className="text-slate-400">Claim:</span> {d.claim}</p>
              {d.legal_hold && <p className="mt-1 flex items-center gap-1 text-[11px] text-red-600"><ShieldAlert size={11} /> Legal hold active — release actions are blocked</p>}
              {d.evidence?.length > 0 && (
                <div className="mt-1.5 text-[11px] text-slate-500" data-testid={`dispute-evidence-${d.dispute_id}`}>
                  <Paperclip size={10} className="inline" /> {d.evidence.length} evidence item(s) (immutable)
                </div>
              )}
              {d.resolution && (
                <div className="mt-1.5 rounded-md bg-emerald-50 p-2 text-[11px] text-emerald-800" data-testid={`dispute-findings-${d.dispute_id}`}>
                  <CheckCircle2 size={11} className="inline" /> Findings: {d.findings} · Outcome: {d.resolution.outcome}
                  {d.resolution.appeal_deadline && <span> · Appeal by {d.resolution.appeal_deadline}</span>}
                </div>
              )}
              {d.resolved_by && <p className="mt-1 text-[11px] text-slate-400">Reviewer: {String(d.resolved_by).slice(0, 10)}</p>}

              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {["open", "appealed"].includes(d.status) && (
                  <button data-testid={`dispute-evidence-btn-${d.dispute_id}`} onClick={() => addEvidence(d)} disabled={busy === `ev-${d.dispute_id}`}
                    className="h-7 rounded border border-slate-300 px-2 text-xs text-slate-600">Add evidence</button>
                )}
                {isCompliance && d.status === "open" && (
                  <button data-testid={`dispute-resolve-btn-${d.dispute_id}`} onClick={() => resolve(d)} disabled={busy === `res-${d.dispute_id}`}
                    className="h-7 rounded bg-slate-800 px-2 text-xs text-white">Resolve (compliance)</button>
                )}
                {d.status === "resolved" && (
                  <button data-testid={`dispute-appeal-btn-${d.dispute_id}`} onClick={() => appeal(d)} disabled={busy === `app-${d.dispute_id}`}
                    className="h-7 rounded border border-slate-300 px-2 text-xs text-slate-600">Appeal</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
